<template>
  <div v-if="props.label" class="section-header" :class="state.sectionHeaderVariant">
    <div class="section-header-text">
      <Heading :content="props.label" :level="props.level" class="section-header-title" />
      <p v-if="description" class="section-header-description">{{ description }}</p>
    </div>
    <Button
      v-if="props.buttonLabel && props.buttonUrl"
      :label="props.buttonLabel"
      :to="props.buttonUrl"
      :icon="props.buttonIcon"
      :variant="state.buttonVariant"
      icon-position="right"
      class="section-header-button"
      :aria-label="`${props.label} overzicht`"
      :analytics="{ linkAnalyticsTag: 'section-header' }"
    />
  </div>
</template>

<script setup lang="ts">
import type { TButtonVariant } from '~~/types/local_types';

const props = defineProps({
  label: {
    type: String,
    default: 'Section Header',
  },
  level: {
    type: Number,
    default: 2,
  },
  buttonLabel: {
    type: String,
    default: 'Meer',
  },
  buttonUrl: {
    type: String,
    default: null,
  },
  buttonIcon: {
    type: String,
    default: 'eenvandaag-general:arrow-right',
  },
  variant: {
    type: String as () => 'inverted' | 'default',
    default: 'default',
  },
  description: {
    type: String,
    default: '',
  },
});

const state = reactive({
  sectionHeaderVariant: `is--${props.variant}`,
  buttonVariant: props.variant === 'inverted' ? 'primary' : ('secondary' as TButtonVariant),
});
</script>

<style lang="postcss" scoped>
.section-header {
  --section-header-gap: var(--unit-16);
  --section-header-margin: 0 0 var(--unit-16) 0;
  --section-header-min-height: 35px;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--section-header-gap);
  margin: var(--section-header-margin);
  min-height: var(--section-header-min-height);

  @media (--lg-viewport) {
    --section-header-gap: var(--unit-20);
    --section-header-margin: 0 0 var(--unit-20) 0;
    --section-header-min-height: 42px;
  }

  &.is--inverted {
    --section-header-margin: 0;
  }
}

.section-header-text {
  --section-header-gap: var(--unit-6);

  display: flex;
  flex-direction: column;
  gap: var(--section-header-gap);

  @media (--md-viewport) {
    --section-header-gap: var(--unit-8);
  }
}

.section-header-title {
  @add-mixin heading-xl;
  color: hsl(var(--color-primary-700));

  .is--inverted & {
    color: hsl(var(--color-accent-bright-blue));
  }
}

.section-header-button {
  flex-shrink: 0;
  align-self: baseline;
}

.section-header-description {
  @mixin body-md {
  }

  .is--inverted & {
    color: hsl(var(--color-primary-200));
  }
}
</style>
